import React, { FC, ClipboardEvent, useRef, useCallback, useEffect } from 'react'
import { Grid, Button, IconButton, InputAdornment, useMediaQuery, Theme } from '@mui/material'
import { Close } from '@mui/icons-material'
import { FormikContext, Form, useFormik } from 'formik'
import { TextField } from 'src/components'
import { SearchParamsKeys } from 'src/enums'
import { insertPastedText } from 'src/helpers'
import { useQueryParams } from 'src/hooks/ui'

interface SearchFormProps {
  placeholder?: string
  // Allow multi-line input (pasting several identifiers, or scanning them one by one with a
  // barcode scanner whose Enter suffix separates and submits — see onEnterPress). Off by default
  // so the shared search field stays a single-line input everywhere except where explicitly enabled.
  multiline?: boolean
}

export const SearchForm: FC<SearchFormProps> = ({ placeholder = 'Enter at least three symbols to start searching', multiline = false }) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [queryParams, setQueryParams, removeQueryParams] = useQueryParams()
  const initialValues = { search: queryParams[SearchParamsKeys.Search] || '' }
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'), { noSsr: true })

  const onSubmit = useCallback(
    async ({ search }: any) => {
      await setQueryParams({
        // Trimmed so scanner/Enter newlines don't leave trailing %0A in the URL
        [SearchParamsKeys.Search]: (search ?? '').trim(),
        [SearchParamsKeys.Page]: '1'
      })
    },
    [setQueryParams]
  )

  const formik = useFormik({
    initialValues,
    onSubmit
  })

  const { values, submitForm, setFieldValue } = formik

  // Single-line mode: Enter submits (unchanged single-term UX). Multiline mode is built for
  // barcode scanners (keyboard emulation, Enter suffix): Enter both inserts a newline — so the
  // next scan lands on its own line — and submits the accumulated list, refreshing the results
  // after every scan. Shift+Enter always inserts a plain newline without submitting.
  const onEnterPress = useCallback(
    (event: any) => {
      const { keyCode, shiftKey, target } = event
      if (keyCode !== 13 || shiftKey) return
      const value = target?.value ?? ''
      if (!multiline) {
        event.preventDefault()
        if (value.length >= 3) submitForm()
        return
      }
      if (!value.trim()) {
        // No leading blank lines from a stray Enter / scanner misfire on an empty field
        event.preventDefault()
        return
      }
      // The newline is inserted by the textarea's default behavior after this handler; the
      // Formik value at this point is still the clean accumulated list, which gets submitted.
      if (value.trim().length >= 3) submitForm()
    },
    [submitForm, multiline]
  )

  // Barcode scanners that emit no Enter suffix "paste" the code instead; without a separator,
  // consecutive scans would concatenate into one garbled identifier. Insert the pasted text
  // manually with a trailing space so every scan stays a distinct identifier. No submit here —
  // the user presses Search (or an Enter-suffix scan submits) when the list is complete.
  const pendingCaret = useRef<number | null>(null)
  const onPaste = useCallback(
    // MUI attaches onPaste to the root element; the event bubbles up from the inner textarea
    (event: ClipboardEvent<HTMLDivElement>) => {
      // Only intercept pastes into the search input itself (not e.g. the clear button, which
      // shares the same MUI root the handler is attached to)
      if (event.target !== inputRef.current) return
      const target = event.target as HTMLTextAreaElement
      const pasted = event.clipboardData.getData('text')
      // Nothing to insert (e.g. an image on the clipboard) — leave the native no-op and, crucially,
      // don't arm pendingCaret: values.search wouldn't change, so the restore effect would not
      // consume it and a stale caret would fire on the next unrelated edit
      if (!pasted) return
      event.preventDefault()
      const { value, caret } = insertPastedText(target.value, pasted, target.selectionStart ?? target.value.length, target.selectionEnd ?? target.value.length)
      pendingCaret.current = caret
      setFieldValue(SearchParamsKeys.Search, value)
    },
    [setFieldValue]
  )

  // Restore the caret after Formik commits the pasted value to the DOM (writing the value moves
  // the caret to the end otherwise), so pasting mid-text doesn't jump the caret
  useEffect(() => {
    if (pendingCaret.current === null) return
    inputRef.current?.setSelectionRange(pendingCaret.current, pendingCaret.current)
    pendingCaret.current = null
  }, [values.search])

  const onReset = useCallback(async () => {
    setFieldValue(SearchParamsKeys.Search, '')
    await removeQueryParams([SearchParamsKeys.Search])
    if (inputRef?.current) inputRef.current.focus()
  }, [setFieldValue, removeQueryParams])

  return (
    <FormikContext.Provider value={formik}>
      <Form>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} sm="auto">
            <TextField
              name="search"
              TextFieldProps={{
                autoFocus: isDesktop,
                autoComplete: 'off',
                onKeyDown: onEnterPress,
                onPaste: multiline ? onPaste : undefined,
                placeholder,
                multiline,
                minRows: multiline ? 2 : undefined,
                maxRows: multiline ? 6 : undefined,
                sx: { width: { xs: '100%', sm: 450 } },
                inputRef,
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {(values?.search?.length > 0 || queryParams[SearchParamsKeys.Search]?.length > 0) && (
                        <IconButton size="small" onClick={onReset}>
                          <Close fontSize="small" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  )
                }
              }}
            />
          </Grid>
          <Grid item>
            <Button type="submit" variant="contained" size="large" disabled={values?.search?.length < 3}>
              Search
            </Button>
          </Grid>
        </Grid>
      </Form>
    </FormikContext.Provider>
  )
}
